/**
 * What account opening contributes, in two groups mounted separately.
 *
 * `onboardingRoutes` is the back office's: the list and the dossier, each
 * behind a permission split by consequence — reading a dossier means reading
 * somebody's identity document, and deciding creates a customer.
 *
 * `publicOnboardingRoutes` is the portal's: one page, `layout: "public"`, no
 * authentication. It takes the tenant because there is no session to read one
 * from, and `signInHref` because the page has to offer a way back to the sign
 * -in form for a visitor who already has an account.
 */

import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { ApplyScreen } from "./screens/ApplyScreen";
import { RequestScreen } from "./screens/RequestScreen";
import { RequestsScreen } from "./screens/RequestsScreen";
import { ONBOARDING_PATHS, ONBOARDING_PUBLIC_PATHS } from "./paths";
import "./i18n";

export function onboardingRoutes(client: ApiClient): FeatureRoutes {
  function Requests() {
    return <RequestsScreen client={client} />;
  }

  function Request() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types. The id is
    // validated by the server on every call it is used in.
    const params: { id?: string } = useParams({ strict: false });
    return <RequestScreen client={client} id={params.id ?? ""} />;
  }

  return {
    id: "onboarding",
    routes: [
      {
        path: ONBOARDING_PATHS.requests,
        component: Requests,
        layout: "app",
        requirement: { authenticated: true, permissions: ["onboarding.request.read"] },
        nav: {
          group: "onboarding:group",
          labelKey: "onboarding:nav.requests",
          section: "main",
          order: 15,
        },
      },
      {
        path: ONBOARDING_PATHS.request,
        component: Request,
        layout: "app",
        requirement: { authenticated: true, permissions: ["onboarding.request.read"] },
      },
    ],
  };
}

export function publicOnboardingRoutes(
  client: ApiClient,
  options: { tenant: string; signInHref?: string },
): FeatureRoutes {
  function Apply() {
    return (
      <ApplyScreen
        client={client}
        tenant={options.tenant}
        {...(options.signInHref ? { signInHref: options.signInHref } : {})}
      />
    );
  }

  return {
    id: "onboarding-public",
    routes: [
      {
        path: ONBOARDING_PUBLIC_PATHS.request,
        component: Apply,
        layout: "public",
        // NOT `anonymousOnly`. A signed-in buyer opening a second account for
        // a sister company is a real errand, and bouncing them home would
        // make them sign out to run it.
      },
    ],
  };
}
